import React, { useState } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { ShoppingBag, Search, MapPin, Instagram, Facebook, Phone, Menu as MenuIcon, X } from "lucide-react";
import { useCart } from "@/lib/cart";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/menu", label: "Menu" },
  { to: "/playground", label: "Playground" },
  { to: "/catering", label: "Catering" },
  { to: "/founder", label: "Founder" },
  { to: "/feedback", label: "Feedback" },
];

export default function Layout() {
  const { count } = useCart();
  const loc = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="min-h-screen font-body text-[#FFFFFF]">
      {/* Header */}
      <header className="sticky top-0 z-40 px-3 pt-3 md:px-4 md:pt-4">
        <div className="max-w-7xl mx-auto glass rounded-full px-4 md:px-6 py-2.5 md:py-3 flex items-center justify-between gap-2">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 min-w-0">
            <span className="font-heading text-lg sm:text-xl md:text-3xl font-semibold tracking-tight truncate">
              Ice Cream Paradise By{" "}
              <span className="text-[#FF69B4]">WOOP</span>
              !!
            </span>
          </Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}
                className={`text-sm tracking-wide uppercase transition-colors ${loc.pathname === n.to ? "text-[#B8E14A]" : "text-[#E6F4F2] hover:text-[#FFFFFF]"}`}>
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/cart" data-testid="nav-cart" className="relative p-2 rounded-full hover:bg-white/10 transition">
              <ShoppingBag className="w-5 h-5" />
              {count > 0 && (
                <span data-testid="cart-badge" className="absolute -top-1 -right-1 bg-[#B8E14A] text-[#1F5F5C] text-[10px] rounded-full w-5 h-5 flex items-center justify-center font-semibold">
                  {count}
                </span>
              )}
            </Link>
            <button data-testid="mobile-menu-toggle" onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-full hover:bg-white/10 transition">
              {mobileOpen ? <X className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {/* Mobile drawer */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              className="lg:hidden max-w-7xl mx-auto glass rounded-3xl px-6 py-4 mt-2">
              <div className="flex flex-col gap-3">
                {NAV.map((n) => (
                  <Link key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                    data-testid={`mnav-${n.label.toLowerCase()}`}
                    className={`text-sm uppercase tracking-wider py-2 ${loc.pathname === n.to ? "text-[#B8E14A]" : "text-white"}`}>
                    {n.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main className="pb-24">
        <Outlet />
      </main>

      {/* Floating cart button */}
      {count > 0 && (
        <motion.div initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="fixed bottom-24 right-6 z-40 md:bottom-8">
          <Link to="/cart" data-testid="floating-cart-btn" className="btn-gold shadow-2xl">
            <ShoppingBag className="w-4 h-4" /> {count} items — View Cart
          </Link>
        </motion.div>
      )}

      {/* Footer */}
      <footer className="bg-[#1F5F5C] text-white px-6 py-16">
        <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-10">
          <div>
            <h4 className="font-heading text-3xl mb-1">
              Ice Cream Paradise By{" "}
              <span className="text-[#FF69B4]">WOOP</span>
              !!
            </h4>
            <div className="text-xs text-[#B8E14A] uppercase tracking-widest mb-3">100% Natural · Handcrafted</div>
            <p className="text-white/70 text-sm leading-relaxed">Premium ice-cream catering for every occasion. Live counters, bulk supply, dessert stations & more.</p>
          </div>
          <div>
            <h5 className="uppercase text-xs tracking-widest text-[#B8E14A] mb-3">Quick Links</h5>
            <ul className="space-y-2 text-sm text-white/80">
              {NAV.map(n => <li key={n.to}><Link to={n.to} className="hover:text-[#B8E14A]">{n.label}</Link></li>)}
            </ul>
          </div>
          <div>
            <h5 className="uppercase text-xs tracking-widest text-[#B8E14A] mb-3">Reach us</h5>
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Marvel Signet Shirur Park Rd Vidyanagar, Hubballi.</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 shrink-0" /> Yash Building Kushgal Rd Opposite Madhura Colony Keshwapur ,Hubbali.</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> <a href="tel:+917899983505" className="hover:text-[#B8E14A]">+91 78999 83505</a></li>
              <li className="text-xs text-white/60 break-all">icecreamparadise.woop@gmail.com</li>
            </ul>
          </div>
          <div>
            <h5 className="uppercase text-xs tracking-widest text-[#B8E14A] mb-3">Staff</h5>
            <div className="text-xs text-white/60 mt-3">
              <Link to="/admin" data-testid="footer-admin-link" className="hover:text-[#B8E14A] mr-4">Admin</Link>
              <Link
                to="/kitchen"
                data-testid="footer-kitchen-link"
                className="hover:text-[#B8E14A] mr-4"
              >
                Kitchen
              </Link>

              <Link
                to="/delivery"
                data-testid="footer-delivery-link"
                className="hover:text-[#B8E14A]"
              >
                Delivery
              </Link>
            </div>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-10 pt-6 border-t border-white/10 text-xs text-white/50">
          © {new Date().getFullYear()} Ice Cream Paradise By{" "}
          <span className="text-[#FF69B4]">WOOP</span>
          !!. All rights reserved.
        </div>
        <div className="max-w-7xl mx-auto mt-4 text-[11px] text-white/60 text-center leading-relaxed">
          <div className="uppercase tracking-widest text-[10px] text-[#B8E14A] mb-1">Developed by</div>
          <div><span className="font-medium text-white">Sumit Raikar</span> · +91 63642 10739 · <a href="mailto:sumitraikar554@gmail.com" className="hover:text-[#B8E14A]">sumitraikar554@gmail.com</a></div>
          <div><span className="font-medium text-white">K Chandana</span> · +91 93537 99298 · <a href="mailto:kchandu12212004@gmail.com" className="hover:text-[#B8E14A]">kchandu12212004@gmail.com</a></div>
        </div>
      </footer>
    </div>
  );
}
